<!--suppress JSUnfilteredForInLoop -->
<template>
  <ProvidePagination
    v-slot="{ activePage, changePage, changePerPage, perPage, totalItems, records }"
    :records="filteredJobs"
    :per-page="itemsPerPage"
  >
    <!-- eslint-disable-next-line vue/no-undef-components -- Baseline eslint ignore please fix if working on this-->
    <lister-template :have-elements="simulatedLoading">
      <template #header>
        <!-- eslint-disable-next-line vue/no-undef-components -- Baseline eslint ignore please fix if working on this-->
        <notifier-growl />
        <div class="col-xs-12">
          <p class="lead">
            {{ simulatedLoading ? textResultsCount : '' }}
          </p>
        </div>
        <div class="col-xs-12 tw-mb-2">
          <WdsInput
            v-model="searchInput"
            class="tw-min-w-[320px]"
            type="text"
            prefix-icon="search"
            :placeholder="$t('Filter jobs by their name/title')"
          />
        </div>
        <div class="col-xs-12 tw-mb-2">
          <div class="tw-grid tw-grid-cols-4 tw-gap-1">
            <WdsSelect
              :list="statusOptions"
              :start-selected="selectedStatusFilter"
              @select-option="updateStatusFilter"
            />
            <WdsSelect
              :list="filterOptions"
              :start-selected="selectedFilter"
              @select-option="updateFilter"
            />
            <WdsSelect
              :list="locations"
              :start-selected="selectedLocation"
              @select-option="updateLocation"
            />
            <WdsSelect
              v-if="teamMembers.length !== 0"
              :list="owners"
              :start-selected="selectedOwner"
              @select-option="updateOwner"
            />
            <WdsSelect
              :list="approvalOptions"
              :start-selected="selectedApproval"
              @select-option="updateApproval"
            />
            <WdsSelect
              :list="sortOptions"
              :start-selected="selectedSort"
              @select-option="updateSort"
            />
            <WdsButtonGhost secondary class="tw-h-[38px]" @click="resetFilter">
              <div class="tw-my-auto">
                {{ $t('Reset filters') }}
              </div>
            </WdsButtonGhost>
          </div>
        </div>
      </template>
      <div class="list-group lg-odd-black card">
        <!-- eslint-disable-next-line vue/no-undef-components -- Baseline eslint ignore please fix if working on this-->
        <list-animation>
          <!-- eslint-disable-next-line vue/no-undef-components -- Baseline eslint ignore please fix if working on this-->
          <list-item-card
            v-for="(job, index) in records"
            :key="job.id"
            :card="job"
            :parent="job.user"
            :index="index"
            :show-skills="false"
            :show-publish-date="true"
            :show-owners="true"
            :avatar-url="job.user ? job.user.picture_url : undefined"
            :show-rate="true"
            :card-title="job.name"
            :card-name="job.user.company"
            :card-destination-url="job.url ? job.url : undefined"
          />
        </list-animation>
      </div>
      <template #footer>
        <div
          v-if="mutatedJobs.length === 0 && simulatedLoading"
          class="no-jobs alert alert-info p-10"
        >
          <p>
            {{ $t('We found no jobs') + ' ' + $t('Change the filter options to see all jobs') }}
          </p>
        </div>
        <WdsPagination
          :page="activePage - 1"
          :number-of-items="totalItems"
          :items-per-page="perPage"
          class="tw-py-4 tw-px-4"
          @update:page="changePage"
          @update:per-page="changePerPage"
        />
      </template>
    </lister-template>
  </ProvidePagination>
</template>

<script>
import { WdsButtonGhost, WdsInput, WdsPagination, WdsSelect } from '@worksome/wds-components'
import { $t, $tc } from '@/app-bootstrapping/SetUpInternationalization'
import ProvidePagination from '@/components/ProvidePagination.vue'

const filter = {
  by: {
    none: '#NO_FILTER',

    status: {
      active: 'ACTIVE_JOBS',
      completed: 'COMPLETED_JOBS',
      draft: 'DRAFT_JOBS',
      removed: 'REMOVED_JOBS',
    },

    contracts: {
      has: 'HAS_CONTRACTS',
      not: 'HAS_NO_CONTRACTS',
      bills: 'HAS_CONTRACTS_AND_BILLS',
      notBills: 'HAS_CONTRACTS_AND_NOT_BILLS',
    },

    bids: {
      has: 'HAS_BIDS',
      not: 'HAS_NO_BIDS',
    },

    location: {
      all: 'LOCATION_ALL',
    },

    owner: {
      all: 'OWNERS_ALL',
      my_own: 'OWNERS_MY_OWN',
    },

    approval: {
      APPROVED: 'approved',
      DENIED: 'rejected',
      REQUESTED: 'requested',
      NONE: 'no-approval',
    },
  },
}

// TODO: Migrate this file to vue data tables with base-data-table
const sort = {
  new: 'NEW',
  old: 'OLD',
  fresh: 'FRESH',
  inactive: 'INACTIVE',
  soon: 'SOON',
}

export default {
  name: 'JobsCreatedList',
  components: {
    WdsButtonGhost,
    WdsSelect,
    ProvidePagination,
    WdsInput,
    WdsPagination,
  },
  props: {
    jobs: {
      type: Array,
      required: true,
    },
    teamMembers: {
      type: [Object, Array],
      default() {
        return {}
      },
    },
    startMember: {
      type: [String, Number],
      default: filter.by.owner.all,
    },
    startFilter: {
      type: [String],
      default: null,
    },
  },
  data() {
    return {
      simulatedLoading: true, // If we have finished simulating that we are loading
      simulatedLoadingSpeed: 200, // The simulated loading time of the page in ms
      mutatedJobs: [],

      page: 0,
      itemsPerPage: 50,

      searchInput: '',
      selectedStatusFilter: filter.by.none,
      selectedFilter: filter.by.none,
      selectedLocation: filter.by.location.all,
      selectedOwner: filter.by.owner.all,
      selectedSort: sort.new,
      selectedApproval: filter.by.approval.NONE,

      statusOptions: [
        { value: filter.by.none, label: this.$t('All jobs') },
        { value: filter.by.status.active, label: this.$t('Active jobs') },
        { value: filter.by.status.completed, label: this.$t('Completed jobs') },
        { value: filter.by.status.draft, label: this.$t('Draft postings') },
        { value: filter.by.status.removed, label: this.$t('Removed jobs') },
      ],

      filterOptions: [
        { value: filter.by.none, label: this.$t('All jobs') },
        { value: filter.by.contracts.has, label: this.$t('With contract') },
        { value: filter.by.contracts.not, label: this.$t('Without contract') },
        { value: filter.by.contracts.notBills, label: this.$t('With contract but no bills') },
        { value: filter.by.bids.has, label: this.$t('Job has bids') },
        { value: filter.by.bids.not, label: this.$t('Job has no bids') },
      ],

      locations: [{ value: filter.by.location.all, label: this.$t('All locations') }],

      owners: [
        { value: filter.by.owner.all, label: this.$t('All owners') },
        { value: filter.by.owner.my_own, label: this.$t('My own') },
      ],

      approvalOptions: [
        { value: filter.by.approval.APPROVED, label: this.$t('Approved') },
        { value: filter.by.approval.DENIED, label: this.$t('Approval denied') },
        { value: filter.by.approval.REQUESTED, label: this.$t('Approval requested') },
        { value: filter.by.approval.NONE, label: this.$t('All approvals') },
      ],

      sortOptions: [
        { value: sort.new, label: this.$t('Newest jobs') },
        { value: sort.old, label: this.$t('Oldest jobs') },
        { value: sort.fresh, label: this.$t('Newest bids') },
        { value: sort.inactive, label: this.$t('Oldest bids') },
        { value: sort.soon, label: this.$t('Start date') },
      ],
    }
  },

  computed: {
    textResultsCount() {
      const countResults = this.simulatedLoading ? this.mutatedJobs.length : 0
      return $tc(':count job found | :count jobs found', countResults)
    },

    filteredJobs() {
      const jobs = this.mutatedJobs

      if (!this.searchInput) {
        return jobs
      }

      return jobs.filter((job) => job.name.toLowerCase().includes(this.searchInput.toLowerCase()))
    },
  },

  created() {
    for (const job of this.jobs) {
      if (job.location) {
        this.locations[job.location] = job.location
      }
    }

    for (const member in this.teamMembers) {
      // eslint-disable-next-line eqeqeq -- Baseline eslint ignore please fix if working on this
      if (member != currentUser.id) {
        // If we don't add the _ the way that the options are listed is reversed and that looks weird.
        // adding the _ makes sure that our list is in order
        this.owners[`_${member.toString()}`] = this.teamMembers[member]
      }
    }

    if (this.startMember !== filter.by.owner.all && this.startMember !== '') {
      this.selectedOwner =
        typeof this.startMember !== 'string' || this.startMember[0] !== '_'
          ? `_${this.startMember}`
          : this.startMember
    }

    if (this.startFilter) {
      if (this.startFilter in this.filterOptions) {
        this.selectedFilter = this.startFilter
      }
    }

    this.mutatedJobs = this.updateJobs()
  },

  methods: {
    $t,

    resetFilter() {
      this.searchInput = ''
      this.selectedStatusFilter = filter.by.none
      this.selectedFilter = filter.by.none
      this.selectedLocation = filter.by.location.all
      this.selectedOwner = filter.by.owner.all
      this.selectedSort = sort.new
      this.selectedApproval = filter.by.approval.NONE

      this.mutatedJobs = this.updateJobs()
    },

    updateStatusFilter(selected) {
      this.selectedStatusFilter = selected.value
      this.mutatedJobs = this.updateJobs()
    },

    updateSort(select) {
      this.selectedSort = select.value
      this.mutatedJobs = this.updateJobs()
    },

    updateFilter(select) {
      this.selectedFilter = select.value
      this.mutatedJobs = this.updateJobs()
    },

    updateLocation(select) {
      this.selectedLocation = select.value
      this.mutatedJobs = this.updateJobs()
    },

    updateOwner(select) {
      this.selectedOwner = select.value
      this.mutatedJobs = this.updateJobs()
    },

    updateApproval(select) {
      this.selectedApproval = select.value
      this.mutatedJobs = this.updateJobs()
    },

    sortJobs(jobs) {
      switch (this.selectedSort) {
        case sort.new:
          return jobs.sort(
            (jobA, jobB) => -(new Date(jobA.published_at) - new Date(jobB.published_at)),
          )
        case sort.old:
          return jobs.sort(
            (jobA, jobB) => new Date(jobA.published_at) - new Date(jobB.published_at),
          )
        case sort.fresh:
          return jobs.sort((jobA, jobB) => -(new Date(jobA.bid_date) - new Date(jobB.bid_date)))
        case sort.inactive:
          return jobs.sort((jobA, jobB) => new Date(jobA.bid_date) - new Date(jobB.bid_date))
        case sort.soon:
          return jobs.sort((jobA, jobB) => -(new Date(jobA.date_start) - new Date(jobB.date_start)))
        default:
          // Error, option does not exist
          return jobs
      }
    },

    filterByStatus(jobs) {
      switch (this.selectedStatusFilter) {
        case filter.by.status.active:
          return jobs.filter((j) => j.completed_step === 'all' && j.is_removed === 0 && ! j.is_completed)

        case filter.by.status.completed:
          return jobs.filter((j) => j.is_completed)

        case filter.by.status.draft:
          return jobs.filter((j) => j.isDraft)

        case filter.by.status.removed:
          return jobs.filter((j) => j.is_removed === 1)

        default:
          return jobs
      }
    },

    filterJobs(jobs) {
      switch (this.selectedFilter) {
        case filter.by.none:
          return jobs.slice()
        case filter.by.contracts.has:
          return jobs.filter((j) => j.has_contract)
        case filter.by.contracts.not:
          return jobs.filter((j) => !j.has_contract)
        case filter.by.contracts.notBills:
          return jobs.filter((j) => j.has_contract && !j.has_bill)
        case filter.by.bids.has:
          return jobs.filter((j) => j.bid_date !== null)
        case filter.by.bids.not:
          return jobs.filter((j) => j.bid_date === null)

        default:
          return jobs
      }
    },

    filterByLocations(jobs) {
      if (this.selectedLocation === filter.by.location.all) return jobs
      return jobs.filter((j) => j.location === this.selectedLocation)
    },

    filterByOwners(jobs) {
      if (this.selectedOwner === filter.by.owner.all) return jobs

      let id = null

      if (this.selectedOwner === filter.by.owner.my_own) {
        // eslint-disable-next-line prefer-destructuring -- Baseline eslint ignore please fix if working on this
        id = currentUser.id
      } else {
        // We remove the _ so we can get the id and then parse it to the int
        id = parseInt(this.selectedOwner.substr(1), 10)
      }

      // we check with undefined since it might return 0 and therefore mistakenly filter a correct find out
      return jobs.filter((j) => j.owners.find((owner) => owner.id === id) !== undefined)
    },

    filterByApprovals(jobs) {
      if (this.selectedApproval === filter.by.approval.NONE) return jobs

      return jobs.filter((job) => job.approval_state === this.selectedApproval)
    },

    updateJobs() {
      this.simulatedLoading = false

      // The timer used for simulating that we are doing a hard search right now
      // Makes the experience better for people
      setTimeout(() => {
        this.simulatedLoading = true
      }, this.simulatedLoadingSpeed)

      this.page = 0
      return this.sortJobs(
        this.filterByOwners(
          this.filterByStatus(
            this.filterJobs(this.filterByLocations(this.filterByApprovals(this.jobs))),
          ),
        ),
      )
    },
  },
}
</script>
